import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, Building2, FileText, ArrowUpRight, MapPin, Landmark, Newspaper } from 'lucide-react';
import { getLatestMarketUpdate } from '../../data/marketUpdateData';
import { currentMonthYear } from '../../utils/seoHelpers';
import { useUserState } from '../../lib/userState';

const API = process.env.REACT_APP_BACKEND_URL;

// Count-up for stat values that may be strings with multiple numeric tokens
// (e.g. "$2,150 - $2,850", "65,000+"). Every digit group animates 0 -> target
// on scroll-in while $ , - + % and words are preserved. Non-numeric values
// (e.g. "FHOG", "Curated H&L") render statically. Date tiles pass animate={false}.
const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

// Community members: a defensible, slowly-growing public figure. Anchored at
// 65,000 on 1 Jun 2026 and ticking up a few members/day so the homepage feels
// live. Deterministic (day-based) so it never jitters between refreshes.
const MEMBER_ANCHOR = Date.UTC(2026, 5, 1);
const MEMBER_BASE = 65000;
const MEMBER_PER_DAY = 5;
const memberCount = () => {
  const days = Math.max(0, Math.floor((Date.now() - MEMBER_ANCHOR) / 86400000));
  return (MEMBER_BASE + days * MEMBER_PER_DAY).toLocaleString('en-AU');
};

const AnimatedValue = ({ text, animate = true, duration = 1600, className = '' }) => {
  const ref = React.useRef(null);
  const [progress, setProgress] = useState(animate ? 0 : 1);
  const started = React.useRef(false);

  useEffect(() => {
    if (!animate) { setProgress(1); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setProgress(1); return undefined; }
    const start = () => {
      if (started.current) return;
      started.current = true;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        setProgress(easeOutQuart(p));
        if (p < 1) requestAnimationFrame(tick);
        else setProgress(1);
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { start(); io.disconnect(); } },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animate, duration, text]);

  const parts = String(text).split(/(\d[\d,]*)/);

  // Non-animated values (dates like "Feb 2026", words like "FHOG") render
  // verbatim - never run them through the thousands formatter (which would
  // turn 2026 into "2,026").
  if (!animate) {
    return <span ref={ref} className={className}>{text}</span>;
  }

  const animatedParts = parts.map((part, i) => {
    if (/^\d[\d,]*$/.test(part)) {
      const target = parseInt(part.replace(/,/g, ''), 10);
      const cur = Math.round(target * progress);
      return <span key={i}>{cur.toLocaleString('en-AU')}</span>;
    }
    return <span key={i}>{part}</span>;
  });

  // Zero layout shift: an invisible copy of the FINAL value reserves the
  // exact width/height (incl. any line wrap) so the counting number - which
  // starts with fewer digits - never reflows or grows the tile mid-animation.
  return (
    <span ref={ref} className={`relative block tabular-nums ${className}`} aria-label={String(text)}>
      <span aria-hidden="true" className="invisible">{text}</span>
      <span aria-hidden="true" className="absolute inset-0">{animatedParts}</span>
    </span>
  );
};

/**
 * Live data strip - sits on the homepage between the hero and the
 * existing content.
 *
 * Source priority (highest first):
 *   1. /api/site-pulse (live GSC-derived data; sets the "Live" badge)
 *   2. The latest entry in marketUpdateData.js (auto-rotates each month
 *      when a new market update is added; never goes stale)
 *   3. Hardcoded sensible defaults (only used if both above are missing)
 *
 * Each card is also region-tagged so the section feels current at a
 * glance: title is the current month/year and stats reflect that month.
 */
const buildBaseStats = () => {
  const latest = getLatestMarketUpdate() || {};
  const metrics = Array.isArray(latest.keyMetrics) ? latest.keyMetrics : [];
  const find = (matchers) => metrics.find((m) => matchers.some((p) => p.test(m.label || ''))) || {};
  const buildCost = find([/build cost/i, /sqm/i]);
  const land = find([/land/i, /lot/i, /block/i]);
  const timeframe = find([/timeframe/i, /lead/i, /weeks?/i, /months?/i]);

  // Always reflect the current calendar month/year so the section feels
  // live. The link still routes to the most recent published market
  // update so the user lands on a valid post.
  const now = currentMonthYear();
  const updateSlug = latest.slug ? `/market-update/${latest.slug}` : '/market-update';

  return [
    {
      key: 'build_cost',
      icon: TrendingUp,
      label: 'Adelaide build cost (avg)',
      value: buildCost.value || '$1,950 - $2,850',
      sub: buildCost.change ? `per sqm, ${buildCost.change.toLowerCase()}` : 'per sqm, project home',
      to: '/adelaide-build-cost-report-2026',
      cta: 'View cost report',
    },
    {
      key: 'community_members',
      icon: Users,
      label: 'Members',
      value: `${memberCount()}+`,
      sub: 'across our Facebook communities',
      to: '/community',
      cta: 'Join the community',
    },
    {
      key: 'land_or_capacity',
      icon: FileText,
      label: land.label ? land.label : 'Outer North land',
      value: land.value || '$220,000 - $300,000',
      sub: land.change ? `Trend: ${land.change.toLowerCase()}` : 'finished blocks, growth corridors',
      to: '/cheapest-suburbs-to-build-adelaide',
      cta: 'See land hot-spots',
    },
    {
      key: 'market_update',
      icon: Users,
      label: 'Latest market update',
      value: `${now.monthShort} ${now.year}`,
      sub: timeframe.value ? `Builder lead time: ${timeframe.value}` : 'Adelaide build market state',
      to: updateSlug,
      cta: 'Read latest update',
      noCount: true,
    },
  ];
};

// Victoria stats set - four tiles that reflect what BuildPilot
// actually ships for VIC today (live H&L inventory, Melbourne
// council coverage, state-grant editorial, latest VIC read). No
// live GSC pulse for VIC yet, so numbers below are the current
// site-authored figures - refreshed here when new content lands.
const buildVicStats = () => {
  const now = currentMonthYear();
  return [
    {
      key: 'vic_packages',
      icon: Building2,
      label: 'Live in Victoria',
      value: 'Curated H&L',
      sub: 'house and land packages',
      to: '/vic',
      cta: 'Browse packages',
    },
    {
      key: 'vic_councils',
      icon: Landmark,
      label: 'Melbourne councils',
      value: 'Inner & Outer',
      sub: 'permit + overlay guides',
      to: '/vic',
      cta: 'Open council guides',
    },
    {
      key: 'vic_grants',
      icon: FileText,
      label: 'Grants + concessions',
      value: 'FHOG',
      sub: '+ stamp duty concession, plain-English',
      to: '/vic/grants',
      cta: 'See grants explainer',
    },
    {
      key: 'vic_latest',
      icon: Newspaper,
      label: 'Latest Victoria read',
      value: `${now.monthShort} ${now.year}`,
      sub: 'Building in Melbourne 2026',
      to: '/post/building-in-melbourne-2026-what-buildpilot-covers-today',
      cta: 'Read the latest',
      noCount: true,
    },
  ];
};

const formatCompact = (n) => {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `${n}`;
};

const LiveDataStrip = () => {
  const userState = useUserState();
  const isVic = userState === 'VIC';
  const [stats, setStats] = useState(() => (isVic ? buildVicStats() : buildBaseStats()));
  const now = currentMonthYear();
  const monthLabel = isVic
    ? `What we cover in ${now.month} ${now.year}`
    : `What we are seeing in ${now.month} ${now.year}`;
  const reportHref = isVic ? '/vic' : '/adelaide-build-cost-report-2026';
  const reportLabel = isVic ? 'Open the Victoria hub' : 'See the full cost report';

  // Re-hydrate the tiles when the visitor swaps state so the section
  // is always aligned with the header chip.
  useEffect(() => {
    setStats(isVic ? buildVicStats() : buildBaseStats());
  }, [isVic]);

  useEffect(() => {
    // Live GSC pulse only applies to the SA tile set today. VIC
    // tiles are site-authored and stable.
    if (isVic) return;
    let cancelled = false;
    fetch(`${API}/api/site-pulse`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setStats((prev) =>
          prev.map((s) => {
            // Never let the live pulse re-inject a raw count into the
            // de-numbered trust tile.
            if (s.key === 'community_members') return s;
            const live = data[s.key];
            if (live && (live.value || live.sub)) {
              return { ...s, value: live.value || s.value, sub: live.sub || s.sub };
            }
            return s;
          })
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isVic]);

  return (
    <section className="bg-white py-9 sm:py-12 border-b border-gray-100" data-testid="live-data-strip" data-live-data-state={userState}>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center sm:flex-row sm:items-end sm:justify-between sm:text-left mb-5 sm:mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-[20px] sm:text-[28px] lg:text-[32px] font-bold text-[#1d1d1f] tracking-[-0.02em]">
              {monthLabel}
            </h2>
          </div>
          <Link
            to={reportHref}
            className="text-[12.5px] sm:text-[13.5px] font-medium text-[#1d1d1f] hover:text-[#1d8efe] transition-colors inline-flex items-center gap-1 shrink-0"
          >
            {reportLabel} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Architectural market ledger: one unified hairline-divided band,
            no floating cards, no icon badges. Compact on mobile. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 bg-[#fafbfc] border border-slate-200/90 rounded-2xl overflow-hidden divide-x divide-y divide-slate-200/80 lg:divide-y-0">
          {stats.map((s) => (
            <Link
              key={s.label}
              to={s.to}
              className="group flex flex-col p-4 sm:p-6 hover:bg-white transition-colors duration-200"
              data-testid={`live-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <p className="text-[12px] sm:text-[13px] font-medium text-slate-500 leading-snug mb-2">{s.label}</p>
              <AnimatedValue
                text={s.value}
                animate={!s.noCount}
                className="block text-[19px] sm:text-[24px] font-bold gradient-text-blue tracking-tight leading-[1.15] mb-1"
              />
              <p className="text-[12px] sm:text-[13px] text-slate-400 leading-snug mb-4 sm:mb-5">{s.sub}</p>
              <span className="text-[12px] sm:text-[13px] font-medium text-slate-700 inline-flex items-center gap-1.5 mt-auto group-hover:text-[#1d8efe] transition-colors">
                {s.cta}
                <ArrowUpRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#1d8efe] group-hover:translate-x-0.5 transition-all" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LiveDataStrip;
